import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect } from "react";
import { Button, Col, Dropdown, Form, Row } from "react-bootstrap";
import { Controller, useWatch, useFieldArray } from "react-hook-form";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { QuestionProps } from "../../types/questionTypes";
import { Section } from "../section";
import { QuillEditorWrapper } from "../../styled";

export const Question: React.FC<QuestionProps> = ({
  questionField,
  sectionIndex,
  questionIndex,
  register,
  control,
  errors,
  availableQuestionTypes,
  showDescription,
  toggleDescription,
  removeQuestion,
  duplicateQuestion,
}) => {
  const questionType = useWatch({
    control,
    name: `sections.${sectionIndex}.questions.${questionIndex}.type`,
  });

  const {
    fields: optionFields,
    append: appendOption,
    remove: removeOption,
  } = useFieldArray({
    control,
    name: `sections.${sectionIndex}.questions.${questionIndex}.options`,
  });

  const showOptions =
    questionType?.value === "select" ||
    questionType?.value === "radio-group" ||
    questionType?.value === "checkbox-group";

  const addOption = () => {
    appendOption({ value: "" });
  };

  // Initialize with one option when switching to a question type that needs options
  useEffect(() => {
    if (showOptions && optionFields.length === 0) {
      appendOption({ value: "" });
    }
  }, [showOptions, optionFields.length, appendOption]);

  return (
    <Section
      key={questionField.id}
      isOpen={true}
      label={`Pergunta ${questionIndex + 1}`}
      hasBorder={true}
      hasMarginTop={false}
      showActions={true}
      onDelete={() => removeQuestion(questionIndex)}
      onDuplicate={() => duplicateQuestion(questionIndex)}
    >
      <>
        <Row>
          <Col>
            <Form.Group
              controlId={`sections.${sectionIndex}.questions.${questionIndex}.name`}
            >
              <Form.Label required>Nome da pergunta</Form.Label>
              {showOptions ? (
                <>
                  <Form.Control
                    {...register(
                      `sections.${sectionIndex}.questions.${questionIndex}.name`
                    )}
                    isInvalid={
                      !!errors.sections?.[sectionIndex]?.questions?.[
                        questionIndex
                      ]?.name
                    }
                    className="mb-3"
                  />
                  <Form.Control.Feedback type="invalid">
                    {
                      errors.sections?.[sectionIndex]?.questions?.[
                        questionIndex
                      ]?.name?.message
                    }
                  </Form.Control.Feedback>

                  <div>
                    {optionFields.map((field, optionIndex) => (
                      <div key={field.id} className="mb-2">
                        <Form.Label>Opção {optionIndex + 1}</Form.Label>
                        <div style={{ display: "flex", gap: "8px" }}>
                          <Form.Control
                            {...register(
                              `sections.${sectionIndex}.questions.${questionIndex}.options.${optionIndex}.value`
                            )}
                            placeholder="Digite a descrição do critério"
                            isInvalid={
                              !!errors.sections?.[sectionIndex]?.questions?.[
                                questionIndex
                              ]?.options?.[optionIndex]?.value
                            }
                          />
                          {optionFields.length > 1 && (
                            <Button
                              variant="link"
                              onClick={() => removeOption(optionIndex)}
                              className="text-decoration-none"
                            >
                              <FontAwesomeIcon icon={["fas", "close"]} />
                            </Button>
                          )}
                        </div>
                        <Form.Control.Feedback type="invalid">
                          {
                            errors.sections?.[sectionIndex]?.questions?.[
                              questionIndex
                            ]?.options?.[optionIndex]?.value?.message
                          }
                        </Form.Control.Feedback>
                      </div>
                    ))}
                    <Button
                      variant="link"
                      onClick={addOption}
                      className="d-flex align-items-center gap-2 text-decoration-none p-0"
                      style={{marginTop: "8px"}}
                    >
                      <FontAwesomeIcon icon={["fas", "plus"]} />
                      Adicionar opção
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <Form.Control
                    {...register(
                      `sections.${sectionIndex}.questions.${questionIndex}.name`
                    )}
                    isInvalid={
                      !!errors.sections?.[sectionIndex]?.questions?.[
                        questionIndex
                      ]?.name
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {
                      errors.sections?.[sectionIndex]?.questions?.[
                        questionIndex
                      ]?.name?.message
                    }
                  </Form.Control.Feedback>
                </>
              )}
            </Form.Group>
          </Col>

          <Col lg={3}>
            <Form.Group
              controlId={`sections.${sectionIndex}.questions.${questionIndex}.type`}
            >
              <Form.Label required>Tipo de pergunta</Form.Label>
              <Controller
                control={control}
                name={`sections.${sectionIndex}.questions.${questionIndex}.type`}
                render={({ field }) => (
                  <Dropdown
                    className="w-100"
                    onSelect={(eventKey: string | null) => {
                      if (eventKey) {
                        const selected = availableQuestionTypes.find(
                          (t) => t.value === eventKey
                        );
                        if (selected) field.onChange(selected);
                      }
                    }}
                  >
                    <Dropdown.Toggle
                      variant="transparent"
                      className="w-100 d-flex align-items-center justify-content-between text-start button-question-type"
                      style={{ borderColor: "var(--input-border)" }}
                    >
                      {field.value ? (
                        <span className="d-flex align-items-center gap-2">
                          <FontAwesomeIcon icon={field.value.icon} />
                          {field.value.label}
                        </span>
                      ) : (
                        "Tipo de pergunta"
                      )}
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="w-100">
                      {availableQuestionTypes.map((type) => (
                        <Dropdown.Item key={type.value} eventKey={type.value}>
                          <FontAwesomeIcon icon={type.icon} className="me-2" />
                          {type.label}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                )}
              />
              <Form.Control.Feedback type="invalid" className="d-block">
                {
                  errors.sections?.[sectionIndex]?.questions?.[questionIndex]
                    ?.type?.message
                }
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        {!showDescription && !showOptions && (
          <Row>
            <Col>
              <Button
                variant="link"
                onClick={() => toggleDescription(questionIndex, true)}
                className="d-flex align-items-center gap-2 text-decoration-none"
              >
                <FontAwesomeIcon icon={["fas", "plus"]} />
                Adicionar Descrição
              </Button>
            </Col>
          </Row>
        )}

        {showDescription && (
          <Row>
            <Col>
              <Form.Group
                controlId={`sections.${sectionIndex}.questions.${questionIndex}.description`}
              >
                <Form.Label>Descrição</Form.Label>
                <div style={{ display: "flex" }}>
                  <Form.Control
                    {...register(
                      `sections.${sectionIndex}.questions.${questionIndex}.description`
                    )}
                    isInvalid={
                      !!errors.sections?.[sectionIndex]?.questions?.[
                        questionIndex
                      ]?.description
                    }
                  />
                  <Button
                    variant="link"
                    onClick={() => toggleDescription(questionIndex, false)}
                    className="d-flex align-items-center gap-2 text-decoration-none"
                  >
                    <FontAwesomeIcon icon={["fas", "close"]} />
                  </Button>
                </div>
                <Form.Control.Feedback type="invalid">
                  {
                    errors.sections?.[sectionIndex]?.questions?.[questionIndex]
                      ?.description?.message
                  }
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        )}

        <Row>
          <Col lg={3}>
            <Form.Group
              controlId={`sections.${sectionIndex}.questions.${questionIndex}.isRequired`}
            >
              <Form.Check
                {...register(
                  `sections.${sectionIndex}.questions.${questionIndex}.isRequired`
                )}
                type="switch"
                label="A pergunta é obrigatória?"
              />
            </Form.Group>
          </Col>

          <Col lg={3}>
            <Form.Group
              controlId={`sections.${sectionIndex}.questions.${questionIndex}.isHiddenFromReviwers`}
            >
              <Form.Check
                {...register(
                  `sections.${sectionIndex}.questions.${questionIndex}.isHiddenFromReviwers`
                )}
                type="switch"
                label="Pergunta oculta para avaliadores?"
              />
            </Form.Group>
          </Col>
        </Row>
      </>
    </Section>
  );
};
